import { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, IconButton, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel,
  Switch, Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

const emptyForm = { code: '', name: '', description: '', statusActive: true };

/**
 * Centros de costo del cliente (spec 3.1.8: múltiples por RUT — criterio 1).
 * El código lo asigna finanzas y se digita (decisión 7, sin flujo de aprobación).
 */
const ErpCostCentersTab = ({ clientId }) => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();

  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // uid en edición o null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/erp/clients/${clientId}/cost-centers`, {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data.response?.status_response === 'OK') {
        setCostCenters(response.data.response.data || []);
      } else {
        setError(t('erp.costCenters.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.costCenters.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization && clientId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, clientId]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (costCenter) => {
    setEditing(costCenter.uid);
    setForm({
      code: costCenter.code || '',
      name: costCenter.name || '',
      description: costCenter.description || '',
      statusActive: costCenter.status === 'active',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      showAlert(t('erp.common.requiredFields'), 'warning');
      return;
    }

    const payload = {
      organizationId: organization,
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description || null,
      status: form.statusActive ? 'active' : 'inactive',
    };

    try {
      setSaving(true);

      const response = editing
        ? await api.put(`/erp/cost-centers/${editing}`, payload, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
        : await api.post(`/erp/clients/${clientId}/cost-centers`, payload, {
            headers: { Authorization: `Bearer ${authToken}` },
          });

      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.costCenters.saveSuccess'), 'success');
        setDialogOpen(false);
        load();
      } else {
        showAlert(t('erp.costCenters.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.errorMessage || '';
      if (message.includes('already exists')) {
        showAlert(t('erp.costCenters.duplicateCode'), 'error');
      } else {
        showAlert(t('erp.costCenters.saveError'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
        >
          {t('erp.costCenters.add')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : costCenters.length === 0 ? (
        <Alert severity="info">{t('erp.costCenters.empty')}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('erp.costCenters.table.code')}</TableCell>
                <TableCell>{t('erp.costCenters.table.name')}</TableCell>
                <TableCell>{t('erp.costCenters.table.description')}</TableCell>
                <TableCell>{t('erp.costCenters.table.status')}</TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {costCenters.map((costCenter) => (
                <TableRow key={costCenter.uid} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{costCenter.code}</TableCell>
                  <TableCell>{costCenter.name}</TableCell>
                  <TableCell>{costCenter.description || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={costCenter.status === 'active'
                        ? t('erp.common.active')
                        : t('erp.common.inactive')}
                      color={costCenter.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('erp.costCenters.edit')}>
                      <IconButton size="small" onClick={() => openEdit(costCenter)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? t('erp.costCenters.edit') : t('erp.costCenters.add')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField
            fullWidth
            size="small"
            required
            label={t('erp.costCenters.form.code')}
            helperText={t('erp.tooltips.costCenter.code')}
            value={form.code}
            onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
          />
          <TextField
            fullWidth
            size="small"
            required
            label={t('erp.costCenters.form.name')}
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label={t('erp.costCenters.form.description')}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          />
          {editing && (
            <FormControlLabel
              control={(
                <Switch
                  checked={form.statusActive}
                  onChange={(e) => setForm((p) => ({ ...p, statusActive: e.target.checked }))}
                />
              )}
              label={form.statusActive ? t('erp.common.active') : t('erp.common.inactive')}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}
          >
            {saving ? t('erp.common.saving') : t('erp.common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpCostCentersTab;
